import { NavLink, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard,
  School,
  FileText,
  ListTree,
  DollarSign,
  Search,
  MessageSquare,
  CalendarClock,
  UserCircle,
  LogOut,
  Home,
  ChevronLeft,
  ChevronRight,
  Bell,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { UserAuth } from "../../context/AuthContext";
import { UserProfile } from "../../context/ProfileContext";
import { AvatarDisplay } from "../student/profile/AvatarDisplay";
import { useCounselorNotifications } from "../../hooks/useCounselorNotifications";
import { useOverflow } from "../../hooks/useOverflow";
import type { StudentNotification } from "../../services/notificationsApi";

function formatRelativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60_000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  return `${Math.floor(hours / 24)}d ago`
}

function getCounselorNotifRoute(n: StudentNotification): string {
  // draft_comparison → go to the student's essay in the counselor viewer
  if (n.entity_type === 'essay' && n.entity_id && n.student_id) {
    return `/counselor/essays/${n.student_id}/${n.entity_id}`
  }
  return '/counselor'
}

function CounselorNotificationBell({ collapsed }: { collapsed: boolean }) {
  const { notifications, unreadCount, loading, markRead, markAllRead } = useCounselorNotifications()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const handleClick = (n: StudentNotification) => {
    if (!n.is_read) markRead(n.id)
    setOpen(false)
    navigate(getCounselorNotifRoute(n))
  }

  return (
    <div ref={ref} className="relative w-full">
      <button
        onClick={() => setOpen((v) => !v)}
        className={`relative w-full flex items-center ${collapsed ? 'justify-center' : 'gap-3'} px-4 py-3 rounded-[24px] text-sm font-medium border transition-all duration-150 bg-white text-gray-700 border-gray-200 dark:bg-[#050309] dark:border-[#3f3277] dark:text-[#d6cfff] hover:bg-[#ede9fe] dark:hover:bg-[#130b33] hover:border-gray-300 dark:hover:border-[#b193ff] hover:text-gray-900 dark:hover:text-white`}
        aria-label="Notifications"
        title={collapsed ? 'Notifications' : undefined}
      >
        <div className="relative flex-shrink-0">
          <Bell className="h-4 w-4 opacity-90" />
          {unreadCount > 0 && (
            <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-0.5 rounded-full bg-violet-500 text-white text-[9px] font-bold flex items-center justify-center leading-none">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </div>
        <AnimatePresence>
          {!collapsed && (
            <motion.span
              initial={{ opacity: 0, width: 0 }}
              animate={{ opacity: 1, width: 'auto' }}
              exit={{ opacity: 0, width: 0 }}
              className="whitespace-nowrap overflow-hidden"
            >
              Notifications
            </motion.span>
          )}
        </AnimatePresence>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className={`absolute z-50 bottom-full mb-2 w-80 rounded-2xl border border-white/10 bg-[#0e0920]/95 backdrop-blur-xl shadow-2xl overflow-hidden ${collapsed ? 'left-full ml-3 bottom-0' : 'left-0'}`}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/8">
              <span className="text-sm font-semibold text-white">Student Updates</span>
              {unreadCount > 0 && (
                <button
                  onClick={markAllRead}
                  className="text-xs text-violet-400 hover:text-violet-300 transition-colors"
                >
                  Mark all read
                </button>
              )}
            </div>
            <div className="max-h-80 overflow-y-auto divide-y divide-white/5">
              {loading && <p className="px-4 py-6 text-xs text-gray-500 text-center">Loading…</p>}
              {!loading && notifications.length === 0 && (
                <p className="px-4 py-6 text-xs text-gray-500 text-center">No updates yet</p>
              )}
              {!loading && notifications.map((n) => (
                <button
                  key={n.id}
                  onClick={() => handleClick(n)}
                  className={`w-full text-left px-4 py-3 flex gap-3 items-start transition-colors duration-150 hover:bg-violet-500/10 ${n.is_read ? 'opacity-50' : ''}`}
                >
                  <span className="text-base flex-shrink-0 mt-0.5">📝</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-200 leading-snug line-clamp-2">
                      {n.message ?? 'A student submitted a new draft'}
                    </p>
                    <p className="text-[10px] text-gray-500 mt-1">{formatRelativeTime(n.created_at)}</p>
                  </div>
                  {!n.is_read && (
                    <span className="w-2 h-2 rounded-full bg-violet-400 flex-shrink-0 mt-1.5" />
                  )}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

interface CounselorSidebarProps {
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

function CounselorSidebar({
  collapsed = false,
  onToggleCollapse,
}: CounselorSidebarProps) {
  const navigate = useNavigate();
  const { signOut } = UserAuth();
  const profileContext = UserProfile();
  const navListRef = useRef<HTMLUListElement>(null);
  const navOverflow = useOverflow(navListRef);

  const { profile = null, photo = { avatarUrl: null, initials: "CP" } } =
    profileContext || {};

  const { first_name = "Counselor", last_name = "Name" } = profile || {};
  // Fallback initials if not provided in photo object
  const initials =
    photo?.initials || `${first_name[0]}${last_name[0]}`.toUpperCase();

  const navItems = [
    { to: "/counselor", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/counselor/colleges", label: "Colleges", icon: School },
    { to: "/counselor/student-lists", label: "Student Lists", icon: ListTree },
    { to: "/counselor/essays", label: "Essays", icon: FileText },
    { to: "/counselor/supplements", label: "Supplements", icon: ListTree },
    { to: "/counselor/activities", label: "Activities", icon: ListTree },
    {
      to: "/counselor/financial-aid",
      label: "Financial Aid",
      icon: DollarSign,
    },
    { to: "/counselor/research", label: "Research", icon: Search },
    { to: "/counselor/chats", label: "Chats", icon: MessageSquare },
    { to: "/counselor/calendar", label: "Calendar", icon: CalendarClock },
  ];

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate("/");
    } catch (error) {
      console.log("error signing out occurred", error);
    }
  };

  return (
    <nav
      data-overflow={navOverflow}
      className={`sidebar-nav-root flex flex-col h-full bg-white dark:bg-black/45 border border-gray-200 dark:border-violet-500/15 rounded-3xl backdrop-blur-xl shadow-[0_35px_80px_-60px_rgba(120,90,200,0.5)] transition-[padding] duration-300 ${
        collapsed ? "px-3 items-center" : "px-6"
      }`}
    >
      {/* Logo/Brand — flex: 0 0 auto zone, never shrinks below its content */}
      <div className="sidebar-brand-block w-full flex flex-col mt-2 shrink-0">
        <button
          onClick={() => navigate("/")}
          className={`flex items-center ${
            collapsed ? "justify-center" : "space-x-2"
          } text-gray-400 hover:text-black dark:hover:text-white transition-colors duration-150 group w-full`}
          title={collapsed ? "Back to Home" : undefined}
        >
          <Home className="w-5 h-5 flex-shrink-0 group-hover:-translate-x-1 transition-transform duration-200" />
          <AnimatePresence>
            {!collapsed && (
              <motion.span
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: "auto" }}
                exit={{ opacity: 0, width: 0 }}
                className="text-sm scroll-m-0 whitespace-nowrap overflow-hidden"
              >
                Back to Home
              </motion.span>
            )}
          </AnimatePresence>
        </button>
        <AnimatePresence>
          {!collapsed && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              <div className="sidebar-brand-divider mt-4 border-t border-violet-500/20">
                <h2 className="text-xl font-semibold bg-gradient-to-r from-violet-700 to-violet-400 dark:from-white dark:via-violet-200 dark:to-violet-400 bg-clip-text text-transparent whitespace-nowrap">
                  Counselor Portal
                </h2>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Navigation Items — the only zone allowed to scroll. min-h-0 overrides
          the flex default of min-height:auto (= min-content height), which is
          what let this <ul> refuse to shrink and push the bottom block
          off-screen at reduced window heights. */}
      <ul
        ref={navListRef}
        className={`sidebar-nav-scroll sidebar-nav-list flex-1 min-h-0 overflow-y-auto w-full ${
          collapsed ? "px-1" : ""
        }`}
      >
        {navItems.map((item) => (
          <li key={item.to}>
            <NavLink
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                isActive
                  ? `sidebar-nav-button sidebar-nav-row flex items-center ${collapsed ? "justify-center" : "gap-3"} w-full px-5 rounded-[24px] text-sm font-medium transition-shadow transition-colors duration-200 overflow-hidden border bg-violet-100 text-violet-800 border-violet-200 shadow-sm dark:bg-[#1a0f45] dark:border-[#c9a9ff] dark:text-white dark:shadow-[0_0_10px_rgba(167,128,255,0.65)] group`
                  : `sidebar-nav-button sidebar-nav-row group flex items-center ${collapsed ? "justify-center" : "gap-3"} w-full px-5 rounded-[24px] text-sm font-medium transition-shadow transition-colors duration-200 overflow-hidden border bg-white text-gray-700 border-gray-200 shadow-none dark:bg-[#050309] dark:border-[#3f3277] dark:text-[#d6cfff] hover:bg-[#ede9fe] dark:hover:bg-[#130b33] hover:border-gray-300 dark:hover:border-[#b193ff] hover:text-gray-900 dark:hover:text-white hover:shadow-[0_0_6px_rgba(167,128,255,0.5)]`
              }
              title={collapsed ? item.label : undefined}
            >
              <item.icon className="h-4 w-4 flex-shrink-0 opacity-90 group-hover:opacity-100" />
              <AnimatePresence>
                {!collapsed && (
                  <motion.span
                    initial={{ opacity: 0, width: 0 }}
                    animate={{ opacity: 1, width: "auto" }}
                    exit={{ opacity: 0, width: 0 }}
                    className="whitespace-nowrap overflow-hidden"
                  >
                    {item.label}
                  </motion.span>
                )}
              </AnimatePresence>
              {collapsed && (
                <span className="absolute left-full ml-3 px-3 py-1.5 rounded-lg bg-black/80 border border-white/10 text-xs text-white opacity-0 group-hover:opacity-100 pointer-events-none shadow-xl whitespace-nowrap">
                  {item.label}
                </span>
              )}
            </NavLink>
          </li>
        ))}
      </ul>

      {/* Bottom Section - Profile & Logout */}
      <div
        className={`sidebar-bottom-block shrink-0 w-full ${
          collapsed ? "items-center" : ""
        }`}
      >
        <CounselorNotificationBell collapsed={collapsed} />

        <NavLink
          to="/counselor/profile"
          className={({ isActive }) =>
            isActive
              ? `sidebar-nav-button sidebar-nav-row flex items-center ${collapsed ? "justify-center" : "gap-3"} w-full px-5 rounded-[24px] text-sm font-medium transition-shadow transition-colors duration-200 overflow-hidden border bg-violet-100 text-violet-800 border-violet-200 shadow-sm dark:bg-[#1a0f45] dark:border-[#c9a9ff] dark:text-white dark:shadow-[0_0_10px_rgba(167,128,255,0.65)] group`
              : `sidebar-nav-button sidebar-nav-row group flex items-center ${collapsed ? "justify-center" : "gap-3"} w-full px-5 rounded-[24px] text-sm font-medium transition-shadow transition-colors duration-200 overflow-hidden border bg-white text-gray-700 border-gray-200 shadow-none dark:bg-[#050309] dark:border-[#3f3277] dark:text-[#d6cfff] hover:bg-gray-50 dark:hover:bg-[#130b33] hover:border-gray-300 dark:hover:border-[#b193ff] hover:text-gray-900 dark:hover:text-white hover:shadow-[0_0_6px_rgba(167,128,255,0.5)]`
          }
        >
          {collapsed ? (
            <UserCircle className="w-5 h-5 flex-shrink-0 opacity-90 group-hover:opacity-100" />
          ) : (
            <AvatarDisplay
              avatarUrl={photo.avatarUrl}
              initials={initials}
              size="sm"
              variant="subtle"
            />
          )}
          <AnimatePresence>
            {!collapsed && (
              <motion.div
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: "auto" }}
                exit={{ opacity: 0, width: 0 }}
                className="flex-1 min-w-0 overflow-hidden"
              >
                <p className="truncate whitespace-nowrap text-sm font-semibold">
                  {first_name} {last_name}
                </p>
                <p className="text-xs text-gray-500 whitespace-nowrap">
                  View Profile
                </p>
              </motion.div>
            )}
          </AnimatePresence>
          {collapsed && (
            <span className="absolute left-full ml-3 px-3 py-1.5 rounded-lg bg-black/80 border border-white/10 text-xs text-white opacity-0 group-hover:opacity-100 pointer-events-none shadow-xl whitespace-nowrap">
              Profile
            </span>
          )}
        </NavLink>

        <button
          onClick={handleSignOut}
          className={`relative w-full flex items-center ${
            collapsed ? "justify-center" : "space-x-3"
          } px-4 py-3 rounded-xl text-gray-400 hover:text-red-300 hover:bg-red-500/10 transition-all duration-150`}
        >
          <LogOut className="w-5 h-5 flex-shrink-0" />
          <AnimatePresence>
            {!collapsed && (
              <motion.span
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: "auto" }}
                exit={{ opacity: 0, width: 0 }}
                className="text-sm font-medium whitespace-nowrap overflow-hidden"
              >
                Sign Out
              </motion.span>
            )}
          </AnimatePresence>
          {collapsed && (
            <span className="absolute left-full ml-3 px-3 py-1.5 rounded-lg bg-black/80 border border-white/10 text-xs text-white opacity-0 group-hover:opacity-100 pointer-events-none shadow-xl whitespace-nowrap">
              Sign Out
            </span>
          )}
        </button>

        {onToggleCollapse && (
          <button
            onClick={() => onToggleCollapse?.()}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm text-[#A1A1AA] hover:text-white hover:border-violet-400/50 hover:bg-white/10 transition"
            aria-label="Toggle sidebar"
          >
            {collapsed ? (
              <ChevronRight className="w-4 h-4" />
            ) : (
              <ChevronLeft className="w-4 h-4" />
            )}
            <AnimatePresence>
              {!collapsed && (
                <motion.span
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: "auto" }}
                  exit={{ opacity: 0, width: 0 }}
                  className="whitespace-nowrap overflow-hidden"
                >
                  Collapse
                </motion.span>
              )}
            </AnimatePresence>
          </button>
        )}
      </div>
    </nav>
  );
}

export default CounselorSidebar;
